import { useState, useEffect } from 'react';
import { SaveIcon, BellIcon, GlobeIcon, LockIcon } from 'lucide-react';
import AccessManagement from '../components/settings/AccessManagement';
import { useAuth } from '../contexts/AuthContext';
import { canManageRoles } from '../lib/permissions';

const Settings = () => {
  const { user, userRole } = useAuth();
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [dashboardNotifications, setDashboardNotifications] = useState(true);
  const [dataRegion, setDataRegion] = useState('us');
  const [message, setMessage] = useState({ type: '', content: '' });

  // Debug logging
  useEffect(() => {
    console.log('Current user:', user);
    console.log('Current role:', userRole);
    console.log('Can manage roles:', canManageRoles(userRole));
  }, [user, userRole]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate settings save
    setMessage({
      type: 'success',
      content: 'Settings saved successfully'
    });

    // Clear message after 3 seconds
    setTimeout(() => {
      setMessage({ type: '', content: '' });
    }, 3000);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-800 mb-8">Settings</h1>

      {message.content && (
        <div className={`mb-4 p-3 rounded-md ${message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {message.content}
        </div>
      )}

      <div className="space-y-8">
        {/* Notification Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <BellIcon className="h-6 w-6 text-blue-500 mr-2" />
            <h2 className="text-xl font-medium text-gray-800">Notification Settings</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-700">Email Notifications</h3>
                <p className="text-sm text-gray-500">Receive subscription analytics reports via email</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={emailNotifications}
                  onChange={() => setEmailNotifications(!emailNotifications)}
                  className="sr-only peer" 
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-700">Dashboard Notifications</h3>
                <p className="text-sm text-gray-500">Show notifications in the dashboard</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={dashboardNotifications}
                  onChange={() => setDashboardNotifications(!dashboardNotifications)}
                  className="sr-only peer" 
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Data Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <GlobeIcon className="h-6 w-6 text-blue-500 mr-2" />
            <h2 className="text-xl font-medium text-gray-800">Data Settings</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="dataRegion" className="block text-sm font-medium text-gray-700">
                Data Region
              </label>
              <select
                id="dataRegion"
                value={dataRegion}
                onChange={(e) => setDataRegion(e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="us">United States</option>
                <option value="eu">European Union</option>
                <option value="ap">Asia Pacific</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Select the region where your data should be processed
              </p>
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <LockIcon className="h-6 w-6 text-blue-500 mr-2" />
            <h2 className="text-xl font-medium text-gray-800">Security</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-700">Two-Factor Authentication</h3>
              <p className="text-sm text-gray-500 mb-2">Add an extra layer of security to your account</p>
              <button className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                Enable 2FA
              </button>
            </div>
          </div>
        </div>

        {/* Access Management - Only show if user has permission */}
        {canManageRoles(userRole) && (
          <AccessManagement />
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSubmit}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <SaveIcon size={18} className="mr-2" />
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
